import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  ShieldAlert, Loader2, RefreshCw, Plus, AlertTriangle, CheckCircle2, Clock, User, X,
  ChevronDown, ChevronRight, MessageSquare, Code2, History as HistoryIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { useCan } from '@/lib/permissions';

const STATUS_STYLE = {
  open: 'bg-rose-500/10 border-rose-500/30 text-rose-600 dark:text-rose-300',
  triaged: 'bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-300',
  investigating: 'bg-sky-500/10 border-sky-500/30 text-sky-600 dark:text-sky-300',
  resolved: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-300',
  wont_fix: 'bg-muted/40 border-border/40 text-muted-foreground',
};
const STATUS_LABEL = { open: 'Open', triaged: 'Triaged', investigating: 'Investigating', resolved: 'Resolved', wont_fix: "Won't fix" };
const SEV_STYLE = {
  high: 'bg-rose-500/15 border-rose-500/40 text-rose-600 dark:text-rose-300',
  medium: 'bg-amber-500/15 border-amber-500/40 text-amber-600 dark:text-amber-300',
  low: 'bg-muted/40 border-border/40 text-muted-foreground',
};
const STATE_STYLE = {
  ok: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-300',
  warning: 'bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-300',
  blocked: 'bg-rose-500/10 border-rose-500/40 text-rose-600 dark:text-rose-300',
};

export default function DataQualityPage() {
  const canManage = useCan('measures:certify');
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState('');
  const [statusFilter, setStatusFilter] = useState('open');
  const [incidents, setIncidents] = useState([]);
  const [counts, setCounts] = useState({ by_status: {}, by_severity: {}, open_count: 0 });
  const [modelState, setModelState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());
  const toggleExpand = (id) => setExpanded((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const modelName = (id) => (models.find((m) => m.model_id === id)?.name) || id;
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ title: '', severity: 'medium' });

  useEffect(() => {
    api.getSemanticModels().then((r) => {
      const ms = r.models || r || [];
      setModels(ms);
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getDataQualityIncidents(statusFilter, modelId);
      setIncidents(r.incidents || []);
      setCounts({ by_status: r.by_status || {}, by_severity: r.by_severity || {}, open_count: r.open_count || 0, open_high: r.open_high || 0 });
      if (modelId) {
        api.getDqModelState(modelId).then(setModelState).catch(() => setModelState(null));
      } else {
        setModelState(null);
      }
    } catch (e) {
      toast.error(e.message || 'Failed to load incidents');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, modelId]);

  useEffect(() => { load(); }, [load]);

  const withBusy = async (key, fn, okMsg) => {
    setBusy((p) => new Set(p).add(key));
    try { await fn(); if (okMsg) toast.success(okMsg); await load(); }
    catch (e) { toast.error(e.message || 'Action failed'); }
    finally { setBusy((p) => { const s = new Set(p); s.delete(key); return s; }); }
  };

  const transition = (inc, status) => withBusy(`t-${inc.incident_id}`,
    () => api.updateDqIncident(inc.incident_id, { status }), `Moved to ${STATUS_LABEL[status] || status}`);
  const setSeverity = (inc, severity) => withBusy(`s-${inc.incident_id}`,
    () => api.updateDqIncident(inc.incident_id, { severity }), 'Severity updated');
  const assignToMe = (inc) => withBusy(`a-${inc.incident_id}`,
    () => api.updateDqIncident(inc.incident_id, { assignee: 'me' }), 'Assigned');
  const createIncident = () => withBusy('create', async () => {
    if (!form.title.trim()) { throw new Error('Title is required'); }
    await api.createDqIncident({ title: form.title.trim(), severity: form.severity, model_id: modelId || null, source: 'manual' });
    setForm({ title: '', severity: 'medium' });
    setShowNew(false);
  }, 'Incident opened');

  const nextForStatus = (s) => ({
    open: ['triaged', 'investigating', 'wont_fix'],
    triaged: ['investigating', 'resolved', 'wont_fix'],
    investigating: ['resolved', 'wont_fix'],
    resolved: ['open'],
    wont_fix: ['open'],
  }[s] || []);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="data-quality-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-heading font-semibold flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-amber-400" /> Data Quality
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Triage and resolve data-quality incidents. Open incidents mark affected answers Provisional until fixed.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => setShowNew(v => !v)} className="gap-1 text-xs h-7" data-testid="dq-new-incident">
            <Plus className="w-3 h-3" /> New incident
          </Button>
          <Button size="sm" variant="outline" onClick={load} className="gap-1 text-xs h-7" data-testid="dq-refresh">
            <RefreshCw className="w-3 h-3" /> Refresh
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Model</Label>
          <select value={modelId} onChange={(e) => setModelId(e.target.value)}
            className="h-8 rounded-md border border-border/50 bg-background px-2 text-xs" data-testid="dq-model-select">
            <option value="">All models</option>
            {models.map((m) => <option key={m.model_id} value={m.model_id}>{m.name || m.model_id}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-1" data-testid="dq-status-filter">
          {['open', 'resolved', 'all'].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-2.5 py-1 rounded-md text-[11px] border ${statusFilter === s ? 'border-primary text-foreground bg-primary/10' : 'border-border/40 text-muted-foreground'}`}
              data-testid={`dq-filter-${s}`}>
              {s === 'open' ? 'Open' : s === 'resolved' ? 'Resolved' : 'All'}
            </button>
          ))}
        </div>
        <span className="text-[11px] text-muted-foreground" data-testid="dq-counts">
          {counts.open_count} open · {counts.open_high || 0} high
        </span>
        {modelState && (
          <Badge variant="outline" className={`text-[10px] gap-1 ${STATE_STYLE[modelState.state] || ''}`} data-testid="dq-model-state"
            title={(modelState.reasons || []).join(' · ')}>
            {modelState.state === 'ok' ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
            Model DQ: {modelState.state}
          </Badge>
        )}
      </div>

      {showNew && (
        <div className="rounded-xl border border-border/40 bg-card/30 p-3 flex items-end gap-2 flex-wrap" data-testid="dq-new-form">
          <div className="flex-1 min-w-[240px]">
            <Label className="text-[10px] text-muted-foreground">What looks wrong?</Label>
            <input type="text" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="e.g. Written premium for 2024 looks doubled"
              className="w-full h-8 px-2 rounded border border-border/40 bg-background text-xs mt-0.5"
              data-testid="dq-new-title" />
          </div>
          <select value={form.severity} onChange={(e) => setForm(f => ({ ...f, severity: e.target.value }))}
            className="h-8 rounded border border-border/40 bg-background px-2 text-xs" data-testid="dq-new-severity">
            <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
          </select>
          <Button size="sm" onClick={createIncident} disabled={busy.has('create')} className="h-8 text-xs" data-testid="dq-new-submit">
            {busy.has('create') ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Open incident'}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setShowNew(false)} className="h-8 text-xs"><X className="w-3 h-3" /></Button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : incidents.length === 0 ? (
        <div className="text-sm text-muted-foreground py-12 text-center" data-testid="dq-empty">
          No {statusFilter === 'open' ? 'open ' : ''}data-quality incidents. Reject an answer with "the data looks wrong" in AI Explore, or open one above.
        </div>
      ) : (
        <div className="space-y-2" data-testid="dq-list">
          {incidents.map((inc) => (
            <div key={inc.incident_id} className="rounded-xl border border-border/40 bg-card/30 p-3 space-y-2"
              data-testid={`dq-incident-${inc.incident_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <Badge variant="outline" className={`text-[9px] ${STATUS_STYLE[inc.status] || ''}`} data-testid={`dq-status-${inc.incident_id}`}>
                      {STATUS_LABEL[inc.status] || inc.status}
                    </Badge>
                    <Badge variant="outline" className={`text-[9px] ${SEV_STYLE[inc.severity || 'medium'] || ''}`} data-testid={`dq-severity-${inc.incident_id}`}>
                      {(inc.severity || 'medium').toUpperCase()}
                    </Badge>
                    {inc.source === 'explore_reject' && <span className="text-[9px] text-muted-foreground">from AI Explore</span>}
                    {inc.assignee && <span className="text-[9px] text-muted-foreground flex items-center gap-0.5"><User className="w-2.5 h-2.5" />{inc.assignee}</span>}
                  </div>
                  <p className="font-medium text-sm text-foreground">{inc.title || inc.detail || '(no title)'}</p>
                  {(inc.measures?.length > 0 || inc.dimensions?.length > 0) && (
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      affects: {[...(inc.measures || []), ...(inc.dimensions || [])].map(x => String(x).replace(/_/g, ' ')).join(', ')}
                    </p>
                  )}
                  {inc.created_at && <p className="text-[10px] text-muted-foreground flex items-center gap-1 mt-0.5"><Clock className="w-2.5 h-2.5" />{new Date(inc.created_at).toLocaleString()}</p>}
                  <button onClick={() => toggleExpand(inc.incident_id)}
                    className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
                    data-testid={`dq-toggle-${inc.incident_id}`}>
                    {expanded.has(inc.incident_id) ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    {expanded.has(inc.incident_id) ? 'Hide details' : 'Show details'}
                  </button>
                </div>
                {canManage && (
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <div className="flex items-center gap-1 flex-wrap justify-end">
                      {nextForStatus(inc.status).map(ns => (
                        <Button key={ns} size="sm" variant={ns === 'resolved' ? 'default' : 'outline'}
                          onClick={() => transition(inc, ns)} disabled={busy.has(`t-${inc.incident_id}`)}
                          className={`h-6 text-[10px] ${ns === 'resolved' ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : ''}`}
                          data-testid={`dq-to-${ns}-${inc.incident_id}`}>
                          {STATUS_LABEL[ns] || ns}
                        </Button>
                      ))}
                    </div>
                    <div className="flex items-center gap-1">
                      {!inc.assignee && (
                        <Button size="sm" variant="ghost" onClick={() => assignToMe(inc)} disabled={busy.has(`a-${inc.incident_id}`)}
                          className="h-6 text-[10px]" data-testid={`dq-assign-${inc.incident_id}`}>Assign me</Button>
                      )}
                      <select value={inc.severity || 'medium'} onChange={(e) => setSeverity(inc, e.target.value)}
                        className="h-6 rounded border border-border/40 bg-background px-1 text-[10px]" data-testid={`dq-sev-select-${inc.incident_id}`}>
                        <option value="low">low</option><option value="medium">medium</option><option value="high">high</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
              {expanded.has(inc.incident_id) && (
                <div className="mt-1 rounded-lg border border-border/40 bg-background/50 p-3 space-y-3 text-xs" data-testid={`dq-details-${inc.incident_id}`}>
                  {inc.question && (
                    <div>
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground mb-1"><MessageSquare className="w-3 h-3" /> Question asked</div>
                      <p className="text-foreground" data-testid={`dq-question-${inc.incident_id}`}>“{inc.question}”</p>
                    </div>
                  )}
                  {inc.sql ? (
                    <div>
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground mb-1"><Code2 className="w-3 h-3" /> Query returned</div>
                      <pre className="whitespace-pre-wrap break-words rounded bg-muted/60 p-2 text-[10.5px] font-mono text-foreground overflow-x-auto" data-testid={`dq-query-${inc.incident_id}`}>{inc.sql}</pre>
                    </div>
                  ) : (
                    <div className="text-[11px] text-muted-foreground" data-testid={`dq-query-none-${inc.incident_id}`}>No query was captured for this incident.</div>
                  )}
                  {inc.answer_text && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">AI answer at the time</div>
                      <p className="text-muted-foreground">{inc.answer_text}</p>
                    </div>
                  )}
                  {inc.detail && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">What was reported</div>
                      <p className="text-foreground">{inc.detail}</p>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-x-6 gap-y-1 text-[11px] text-muted-foreground">
                    <span>Model: <span className="text-foreground">{modelName(inc.model_id)}</span></span>
                    {inc.reported_by && <span>Reported by: <span className="text-foreground">{inc.reported_by}</span></span>}
                    {inc.assignee && <span>Assignee: <span className="text-foreground">{inc.assignee}</span></span>}
                    {(inc.measures?.length > 0 || inc.dimensions?.length > 0) && (
                      <span>Affects: <span className="text-foreground">{[...(inc.measures || []), ...(inc.dimensions || [])].map(x => String(x).replace(/_/g, ' ')).join(', ')}</span></span>
                    )}
                  </div>
                  {inc.resolution_note && (
                    <div className="rounded bg-emerald-500/10 border border-emerald-500/30 p-2">
                      <div className="text-[10px] uppercase tracking-wide text-emerald-700 dark:text-emerald-300 mb-0.5">Resolution</div>
                      <p className="text-foreground">{inc.resolution_note}</p>
                    </div>
                  )}
                  {(inc.history?.length > 0) && (
                    <div>
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground mb-1"><HistoryIcon className="w-3 h-3" /> Activity</div>
                      <ul className="space-y-1" data-testid={`dq-history-${inc.incident_id}`}>
                        {inc.history.map((h, i) => (
                          <li key={i} className="flex items-start gap-2 text-[11px]">
                            <span className="text-muted-foreground/70 shrink-0">{h.at ? new Date(h.at).toLocaleString() : ''}</span>
                            <span className="text-foreground">{h.action}{h.by ? ` · ${h.by}` : ''}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export { DataQualityPage };
